import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ScheduleDadjService } from './schedule-d-adj.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-schedule-d-adj',
    templateUrl: './schedule-d-adj.component.html',
    styleUrls: ['./schedule-d-adj.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ScheduleDadjComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  crudLoading = false;
  title = 'Schedule D Adjustments';

  lineOneGLs: any[] = [];
  lineSixGLs: any[] = [];
  lineFourUnusedCapLossCarryover: any = '';
  noLineOneGLRecords = false;
  noLineSixGLRecords = false;

  scheddEditFormFields: any = { TRANS_TYPE_CHK: false, WORTHLESS_FLAG_IND: false, IS_SEC_754_IND: false };
  private scheddEditFormFieldsOrig: any = {};
  formSubmitted = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private scheduleDService: ScheduleDadjService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData;
    this.SERVER_CONFIG = header.serverConfigData;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.lineOneGLs = [];
    this.lineSixGLs = [];
    this.lineFourUnusedCapLossCarryover = '';
    this.noLineOneGLRecords = false;
    this.noLineSixGLRecords = false;
    this.clearScheddEditForm();
    this.getLineOneGLs();
    this.getLineSixGLs();
    this.getLineFourUnusedCapLossCarryover();
  }

  private getLineOneGLs() {
    const params = {
      action_id: 43,
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.rowData.SCENARIO,
      reporting_period: this.rowData.REPORTING_PERIOD,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      bsla_code: this.rowData.BSLA_CODE,
      line_no: 1
    };
    this.scheduleDService.loadJsonObject(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.lineOneGLs = data.jsonObject || [];
          this.noLineOneGLRecords = this.lineOneGLs.length === 0;
        }
        this.cdr.detectChanges();
      }
    );
  }

  private getLineSixGLs() {
    const params = {
      action_id: 43,
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.rowData.SCENARIO,
      reporting_period: this.rowData.REPORTING_PERIOD,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      bsla_code: this.rowData.BSLA_CODE,
      line_no: 6
    };
    this.scheduleDService.loadJsonObject(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.lineSixGLs = data.jsonObject || [];
          this.noLineSixGLRecords = this.lineSixGLs.length === 0;
        }
        this.cdr.detectChanges();
      }
    );
  }

  private getLineFourUnusedCapLossCarryover() {
    const params = {
      action_id: 44,
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.rowData.SCENARIO,
      reporting_period: this.rowData.REPORTING_PERIOD,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      bsla_code: this.rowData.BSLA_CODE
    };
    this.scheduleDService.loadJsonObject(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          const hits = data.jsonObject || [];
          this.lineFourUnusedCapLossCarryover = hits.length === 0 ? 0 : hits[0].RETASFILED_SCHDM_AMT;
        }
        this.cdr.detectChanges();
      }
    );
  }

  loadRowEdit(rowInfo: any) {
    this.scheddEditFormFields = _.cloneDeep(rowInfo);
    this.setScheddEditFormCheckboxes();
    this.scheddEditFormFieldsOrig = _.cloneDeep(this.scheddEditFormFields);
    this.cdr.detectChanges();
  }

  resetScheddEdit() {
    this.scheddEditFormFields = _.cloneDeep(this.scheddEditFormFieldsOrig);
    this.formSubmitted = false;
    this.cdr.detectChanges();
  }

  cancelScheddEdit() {
    this.clearScheddEditForm();
    this.formSubmitted = false;
    this.cdr.detectChanges();
  }

  adjustmentDelete(adjustmentInfo: any) {
    if (adjustmentInfo.LINE_NO === '1' || adjustmentInfo.LINE_NO === 1) {
      const delindex = _.findIndex(this.lineOneGLs, { ROWNUM: adjustmentInfo.ROWNUM });
      if (delindex >= 0) this.lineOneGLs.splice(delindex, 1);
      this.noLineOneGLRecords = this.lineOneGLs.length === 0;
    } else if (adjustmentInfo.LINE_NO === '6' || adjustmentInfo.LINE_NO === 6) {
      const delindex = _.findIndex(this.lineSixGLs, { ROWNUM: adjustmentInfo.ROWNUM });
      if (delindex >= 0) this.lineSixGLs.splice(delindex, 1);
      this.noLineSixGLRecords = this.lineSixGLs.length === 0;
    }
    this.recalcLineGLs();
    this.gtwSnackbarService.openSnackBar(adjustmentInfo.DESCRIPTION + ' has been successfully deleted', 'gtw-snackbar--green');
    this.cdr.detectChanges();
  }

  saveScheddEdit() {
    if (!this.scheddEditFormFields.DESCRIPTION || !this.scheddEditFormFields.DATE_ACQ ||
        !this.scheddEditFormFields.DATE_SOLD || !this.scheddEditFormFields.SALE_PRICE ||
        !this.scheddEditFormFields.COST_BASIS || !this.scheddEditFormFields.GAIN_LOSS ||
        !this.scheddEditFormFields.TRANS_TYPE || !this.scheddEditFormFields.LINE_NO) {
      this.formSubmitted = true;
      this.gtwSnackbarService.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      return;
    }

    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.cdr.detectChanges();

    this.reconcileScheddEditFormCheckboxes();
    const arrayShell = [this.scheddEditFormFields];

    this.scheduleDService.saveScheduleD(this.GENERAL_CONFIG.base_url, arrayShell).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        if (response === '1' || response?.callSuccess === '1') {
          if (this.scheddEditFormFields.ROWNUM === '' || !this.scheddEditFormFields.ROWNUM) {
            this.adjustmentAdded();
          } else {
            this.adjustmentEdited();
          }
          this.recalcLineGLs();
          this.cancelScheddEdit();
        } else {
          this.gtwSnackbarService.openSnackBar('An error occurred while saving', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  private adjustmentAdded() {
    const added = _.cloneDeep(this.scheddEditFormFields);
    added.ROWNUM = -1;
    if (String(added.LINE_NO) === '1') {
      this.lineOneGLs.push(added);
      this.noLineOneGLRecords = false;
    } else if (String(added.LINE_NO) === '6') {
      this.lineSixGLs.push(added);
      this.noLineSixGLRecords = false;
    }
    this.gtwSnackbarService.openSnackBar(added.DESCRIPTION + ' has been successfully added', 'gtw-snackbar--green');
  }

  private adjustmentEdited() {
    const edited = _.cloneDeep(this.scheddEditFormFields);
    if (String(edited.LINE_NO) === '1') {
      const idx = _.findIndex(this.lineOneGLs, { ROWNUM: edited.ROWNUM });
      if (idx >= 0) this.lineOneGLs[idx] = edited;
    } else if (String(edited.LINE_NO) === '6') {
      const idx = _.findIndex(this.lineSixGLs, { ROWNUM: edited.ROWNUM });
      if (idx >= 0) this.lineSixGLs[idx] = edited;
    }
    this.gtwSnackbarService.openSnackBar(edited.DESCRIPTION + ' has been successfully edited', 'gtw-snackbar--green');
  }

  private recalcLineGLs() {
    let newsum = 0;
    this.lineOneGLs.forEach(v => newsum += parseInt(v.GAIN_LOSS) || 0);
    newsum += parseInt(this.lineFourUnusedCapLossCarryover) || 0;
    this.lineSixGLs.forEach(v => newsum += parseInt(v.GAIN_LOSS) || 0);
    this.rowData.AGGR_CGNI = newsum;
    this.rowData.AGGR_DIFF = newsum - (parseInt(this.rowData.AGGR_TB) || 0);
  }

  private clearScheddEditForm() {
    this.scheddEditFormFields = {
      DESCRIPTION: '', DATE_ACQ: '', DATE_SOLD: '', SALE_PRICE: '', COST_BASIS: '',
      GAIN_LOSS: '', TRANS_TYPE: '', TRANS_TYPE_CHK: '', WORTHLESS_FLAG: '', IS_SEC_754: '',
      LINE_NO: '', COMMENTS: '', ROWNUM: '', TRANS_TYPE_CHK_IND: false,
      WORTHLESS_FLAG_IND: false, IS_SEC_754_IND: false
    };
    this.scheddEditFormFieldsOrig = {};
  }

  private setScheddEditFormCheckboxes() {
    this.scheddEditFormFields.TRANS_TYPE_CHK_IND = this.scheddEditFormFields.TRANS_TYPE_CHK === 'Y';
    this.scheddEditFormFields.WORTHLESS_FLAG_IND = this.scheddEditFormFields.WORTHLESS_FLAG === 'Y';
    this.scheddEditFormFields.IS_SEC_754_IND = this.scheddEditFormFields.IS_SEC_754 === 'Y';
  }

  private reconcileScheddEditFormCheckboxes() {
    this.scheddEditFormFields.TRANS_TYPE_CHK = this.scheddEditFormFields.TRANS_TYPE_CHK_IND ? 'Y' : 'N';
    this.scheddEditFormFields.WORTHLESS_FLAG = this.scheddEditFormFields.WORTHLESS_FLAG_IND ? 'Y' : 'N';
    this.scheddEditFormFields.IS_SEC_754 = this.scheddEditFormFields.IS_SEC_754_IND ? 'Y' : 'N';
  }

  formatNumber(val: any): string {
    if (val === null || val === undefined || val === '') return '';
    const num = Number(val);
    if (isNaN(num)) return String(val);
    return num.toLocaleString();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  closeModal() {
    this.requestGridUpdate.emit('cancel');
  }
}
